import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef, useState, KeyboardEvent } from 'react'
import Select, { GroupBase, MultiValue, SelectComponentsConfig, SingleValue } from 'react-select'

import './SelectInput.css'
import { cx } from '@/utils/cx'

export interface SelectOption {
  value: string
  label: string
}

interface SelectInputProps {
  id?: string
  /** the id of the text input, for a `<label htmlFor>` */
  inputId?: string
  options: SelectOption[]
  value: null | SelectOption | SelectOption[]
  onChange: (value: SingleValue<SelectOption> | MultiValue<SelectOption>) => void
  autoFocus?: boolean
  className?: string
  classNamePrefix?: string
  isClearable?: boolean
  isDisabled?: boolean
  isMulti?: boolean
  isSearchable?: boolean
  loadingMessage?: (obj: { inputValue: string }) => ReactNode
  menuPlacement?: 'bottom' | 'auto' | 'top'
  components?: Partial<SelectComponentsConfig<SelectOption, boolean, GroupBase<SelectOption>>>
  defaultMenuIsOpen?: boolean
  placeholder?: string
  showCheckmark?: boolean
  style?: CSSProperties
}

export const SelectInput = ({
  id,
  inputId,
  options,
  value,
  onChange,
  autoFocus = false,
  className,
  classNamePrefix,
  isClearable = false,
  isDisabled = false,
  isMulti = false,
  isSearchable = false,
  loadingMessage,
  menuPlacement = 'auto',
  components,
  defaultMenuIsOpen = false,
  placeholder = 'Select',
  showCheckmark,
  style,
}: SelectInputProps) => {
  const [selectValue, setSelectValue] = useState<
    null | MultiValue<SelectOption> | SingleValue<SelectOption>
  >(null)

  function handleChange(newValue: MultiValue<SelectOption> | SingleValue<SelectOption>) {
    setSelectValue(newValue)
    onChange(newValue)
  }

  useEffect(() => setSelectValue(value), [value])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null)

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      selectRef?.current?.onMenuOpen()
    }
  }

  return (
    <div
      className={cx(
        'select-input__container',
        'onboarding-select-input',
        showCheckmark && 'select-input__show-checkmark'
      )}
      style={style}
    >
      <Select
        id={id ?? 'select-input'}
        inputId={inputId}
        ref={selectRef}
        onKeyDown={handleKeyDown}
        options={options}
        value={selectValue}
        onChange={handleChange}
        defaultMenuIsOpen={defaultMenuIsOpen}
        className={className ?? 'select-input'}
        classNamePrefix={classNamePrefix || className || 'select-input'}
        autoFocus={autoFocus}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        loadingMessage={loadingMessage ?? (() => <>Fetching options</>)}
        menuPlacement={menuPlacement}
        components={components ?? undefined}
        placeholder={placeholder}
        closeMenuOnSelect
      />
    </div>
  )
}
